import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { authService, UserProfile } from '../services/firebase/auth';
import { isFirebaseConfigured } from '../services/firebase/config';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithGitHub: () => Promise<void>;
  signInAnonymously: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: { displayName?: string; photoURL?: string }) => Promise<void>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updatePreferences: (preferences: Partial<UserProfile['preferences']>) => Promise<void>;
  updateApiKeys: (apiKeys: Partial<UserProfile['apiKeys']>) => Promise<void>;
  trackUsage: (type: 'promptsGenerated' | 'promptsSaved' | 'apiCalls', increment?: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if Firebase is configured
  const firebaseConfigured = isFirebaseConfigured();

  useEffect(() => {
    if (!firebaseConfigured) {
      console.warn('Firebase is not configured. Authentication features will be disabled.');
      setLoading(false);
      return;
    }

    // Subscribe to auth state changes
    const unsubscribe = authService.onAuthStateChange(async (user, profile) => {
      if (!user) {
        // Auto sign in anonymously if no user is signed in
        try {
          console.log('No user found, signing in anonymously...');
          await authService.signInAnonymously();
          // The state change will trigger again with the anonymous user
        } catch (error) {
          console.error('Failed to sign in anonymously:', error);
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      } else {
        setUser(user);
        setProfile(profile);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [firebaseConfigured]);

  // Authentication methods
  const signUp = async (email: string, password: string, displayName?: string) => {
    if (!firebaseConfigured) {
      throw new Error('Firebase is not configured');
    }
    
    try {
      setError(null);
      const { user, profile } = await authService.signUp(email, password, displayName);
      setUser(user);
      setProfile(profile);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    if (!firebaseConfigured) {
      throw new Error('Firebase is not configured');
    }
    
    try {
      setError(null);
      const { user, profile } = await authService.signIn(email, password);
      setUser(user);
      setProfile(profile);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    if (!firebaseConfigured) {
      throw new Error('Firebase is not configured');
    }
    
    try {
      setError(null);
      const { user, profile } = await authService.signInWithGoogle();
      setUser(user);
      setProfile(profile);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const signInWithGitHub = async () => {
    if (!firebaseConfigured) {
      throw new Error('Firebase is not configured');
    }
    
    try {
      setError(null);
      const { user, profile } = await authService.signInWithGitHub();
      setUser(user);
      setProfile(profile);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const signInAnonymously = async () => {
    if (!firebaseConfigured) {
      throw new Error('Firebase is not configured');
    }
    
    try {
      setError(null);
      const { user, profile } = await authService.signInAnonymously();
      setUser(user);
      setProfile(profile);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const signOut = async () => {
    if (!firebaseConfigured) {
      throw new Error('Firebase is not configured');
    }
    
    try {
      setError(null);
      await authService.signOut();
      setUser(null);
      setProfile(null);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    if (!firebaseConfigured) {
      throw new Error('Firebase is not configured');
    }
    
    try {
      setError(null);
      await authService.resetPassword(email);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const updateProfile = async (updates: { displayName?: string; photoURL?: string }) => {
    if (!firebaseConfigured) {
      throw new Error('Firebase is not configured');
    }
    
    try {
      setError(null);
      const updatedUser = await authService.updateProfile(updates);
      setUser(updatedUser);
      
      // Refresh profile
      const updatedProfile = await authService.getCurrentUserProfile();
      setProfile(updatedProfile);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    if (!firebaseConfigured) {
      throw new Error('Firebase is not configured');
    }
    
    try {
      setError(null);
      await authService.updatePassword(currentPassword, newPassword);
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const updatePreferences = async (preferences: Partial<UserProfile['preferences']>) => {
    if (!firebaseConfigured) {
      throw new Error('Firebase is not configured');
    }
    
    try {
      setError(null);
      await authService.updateUserPreferences(preferences);
      
      // Update local profile
      if (profile) {
        setProfile({
          ...profile,
          preferences: { ...profile.preferences, ...preferences }
        });
      }
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const updateApiKeys = async (apiKeys: Partial<UserProfile['apiKeys']>) => {
    if (!firebaseConfigured) {
      throw new Error('Firebase is not configured');
    }
    
    try {
      setError(null);
      await authService.updateApiKeys(apiKeys);
      
      // Update local profile
      if (profile) {
        setProfile({
          ...profile,
          apiKeys: { ...profile.apiKeys, ...apiKeys }
        });
      }
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  const trackUsage = async (
    type: 'promptsGenerated' | 'promptsSaved' | 'apiCalls',
    increment: number = 1
  ) => {
    if (!firebaseConfigured) return;
    
    try {
      await authService.trackUsage(type, increment);
      
      // Update local profile
      if (profile && profile.usage) {
        setProfile({
          ...profile,
          usage: {
            ...profile.usage,
            [type]: (profile.usage[type] || 0) + increment
          }
        });
      }
    } catch (error) {
      console.error('Error tracking usage:', error);
    }
  };

  const value: AuthContextType = {
    user,
    profile,
    loading,
    error,
    isAuthenticated: !!user,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithGitHub,
    signInAnonymously,
    signOut,
    resetPassword,
    updateProfile,
    updatePassword,
    updatePreferences,
    updateApiKeys,
    trackUsage
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;